<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import ButtonLpCta from '@/components/button/ButtonLpCta.vue'
import textHeroPC2 from '@/images/about/text-hero_pc_02.svg'
import textHeroPC3 from '@/images/about/text-hero_pc_03.svg'
import achievementImage01 from '@/images/about/text-achievement_01.svg'
import achievementImage02 from '@/images/about/text-achievement_02.svg'
import achievementImage03 from '@/images/about/text-achievement_03.svg'
import { newUserRegistrationPath } from '@/generated/railsRoutes'

export default defineComponent({
  name: 'AboutHeroB',

  components: {
    ButtonLpCta,
  },

  props: {
    /**
     * A / B テスト用のクエリキー
     */
    query: {
      type: String,
      default: '',
    },

    ctaButtonLabel: {
      type: String,
      required: true,
    },

    trackingCodePrefix: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    // キャッチコピー画像のスライドイン演出は、画像の読み込み完了を起点にする
    const isTextHeroLoaded = ref(false)

    const ACHIEVEMENT_ITEMS = [
      {
        image: achievementImage01,
        label: '累計会員数70万人以上',
      },
      {
        image: achievementImage02,
        label: '経営者や役員20万人以上',
      },

      {
        image: achievementImage03,
        label: '事例記事660社以上',
      },
    ] as const

    const heroTextImageUrl = computed<{ image: string, alt: string }>(() => {
      return props.query === 'mail_campaign'
        ? {
          image: textHeroPC2,
          alt: 'ここでしか読めない 経営者のための事例メルマガが届く',
        }
        : {
          image: textHeroPC3,
          alt: 'ここでしか読めない 経営者のための事例メディア',
        }
    })

    return {
      // constants
      ACHIEVEMENT_ITEMS,

      // routes
      newUserRegistrationPath,

      // data
      isTextHeroLoaded,

      // computed
      heroTextImageUrl,
    }
  },
})
</script>

<template>
  <section class="f-section__hero">
    <div class="f-section__hero--body">
      <div
        :class="[
          'l-unit__hero',
          `is-${query}`,
        ]">
        <div class="l-unit__hero--header">
          <div class="o-heading__page">
            <h1 class="o-heading__page--item">
              <div class="o-figure__catchCopy">
                <img
                  class="o-figure__catchCopy--item"
                  :src="heroTextImageUrl.image"
                  fetchpriority="high"
                  :alt="heroTextImageUrl.alt"
                  :data-loaded="isTextHeroLoaded"
                  data-is-liquid="true"
                  @load="isTextHeroLoaded = true">
              <!-- /.o-figure__catchCopy --></div>
            </h1>
          <!-- /.o-heading__page --></div>
        <!-- /.l-unit__hero header --></div>

        <div class="l-unit__hero--image">
          <div class="o-list__achievement">
            <ul class="o-list__achievement--items">
              <li
                v-for="(item, index) in ACHIEVEMENT_ITEMS"
                :key="item.label"
                class="o-list__achievement--item"
                :data-index="index">
                <p class="o-label__achievement--item">
                  <img
                    class="img"
                    :src="item.image"
                    :alt="item.label">
                </p>
              </li>
            </ul>
          <!-- /.o-list__achievement --></div>
        <!-- /.l-unit__hero image --></div>

        <div class="l-unit__hero--description">
          <div class="o-label__leadCopy">
            <p class="o-label__leadCopy--item">
              他社の成功と失敗から、経営のヒントを毎週お届けします。
            </p>
          <!-- /.o-label__leadCopy --></div>
        <!-- /.l-unit__hero description --></div>

        <div class="l-unit__hero--action">
          <ButtonLpCta
            :href="newUserRegistrationPath({ trcd: `${trackingCodePrefix}_hero` })"
            font-size="xLarge"
            size="normal"
            animate>
            <span
              class="span"
              v-html="ctaButtonLabel" />
          </ButtonLpCta>
        <!-- /.l-unit__hero action --></div>
      <!-- /.l-unit__hero --></div>
    <!-- /.f-section__hero body --></div>
  <!-- /.f-section__hero --></section>
</template>

<style lang="sass" scoped>
$hero-contens-width-pc: 1240

// foundation
.f-section__hero
  min-height: 500px
  position: relative
  padding: 2em #{$global-prop-sp}px
  background:
    image: url('@/images/about/bg-hero_01.jpg')
    size: cover
    position: center
    repeat: no-repeat

  & &--body
    width: 100%
    max-width: #{$hero-contens-width-pc}px
    position: relative
    margin: 0 auto

  +breakpoint($max-media: tablet)
    background:
      color: rgba($bg-white, 1)
      image: linear-gradient(to right, #e2f2ff 0%, $bg-white 70%)

// layout
.l-unit__hero
  $root: &
  display: grid
  grid-template-columns: auto 1fr

  & &--header
    grid-column: 1 / 3
    grid-row: 1 / 2
    margin-bottom: 1em

  & &--image
    grid-column: 2 / 3
    grid-row: 2 / 4
    align-self: end
    margin-left: getRelativeValue(200, $hero-contens-width-pc)

  & &--description
    grid-column: 1 / 2
    grid-row: 2 / 3

  & &--action
    grid-column: 1 / 2
    grid-row: 3 / 4
    margin-top: 1.5em

  & &--image,
  & &--description,
  & &--action
    max-width: 560px

  // クエリパラメータの文字列がそのまま class 名に入るため、キャメルケースでないことに注意
  &.is-mail_campaign
    #{$root}--header
      max-width: 640px

  &.is-media
    #{$root}--header
      max-width: 480px

  +breakpoint($max-media: tablet)
    display: flex
    flex-direction: column

    #{$root}--image,
    #{$root}--description,
    #{$root}--action
      max-width: none

    #{$root}--image
      order: 1
      margin:
        top: 2.5em
        left: 0

    #{$root}--action
      order: 2
      margin-top: 2em

// object
.o-heading__page
  & &--item
    line-height: 1

.o-figure__catchCopy
  & &--item
    display: block
    line-height: 1
    transform: translateX(-32px)
    opacity: 0
    +transition($duration: 2s)

    &[data-loaded="true"]
      transform: translateX(0)
      opacity: 1

.o-label__leadCopy
  $root: &

  &:first-child
    margin-top: 0

  & &--item
    line-height: 1.5
    color: #2c3e50
    font:
      size: $font-x-large
      weight: bold

  +breakpoint($max-media: tablet)
    #{$root}--item
      font-size: $font-normal

.o-list__achievement
  $root: &

  & &--items
    display: grid
    grid-template-columns: repeat(3, minmax(0, 150px))
    align-items: center
    column-gap: 1em

  +breakpoint($max-media: tablet)
    #{$root}--items
      grid-template-columns: repeat(3, 1fr)
      column-gap: getRelativeValue(16, 375)
</style>
